import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  GroupIcon,
  Profile,
  ProfileIcon,
  Team,
} from "../../assets/svg/Svgs";
import {
  APP_NAME,
  BORROWER,
  COMPONENT,
  DATE,
  INDIVIDUAL,
  SAVE,
  SELECT,
  SELECT_VIEW,
  TEXT,
} from "../../constants/constants";
import {
  API_USERS,
  API_UGANDAN_BANKS,
  API_USER_BANK_ACCOUNTS,
  API_USER_MOMO_ACCOUNTS,
} from "../../constants/apis";
import { addSelectOption, getChangedFields } from "../../utils/helpers";
import useGet from "../common/useGet";
import useSet from "../common/useSet";
import { showConfetti } from "../../components/elements/ConfettiView";
import { InfoBox } from "../../components/elements/Elements";
import { useAuth } from "../../components/context/AuthContext";
import { PRIMARY_COLOR, PRIMARY_LIGHT } from "../../constants/colors";
import { IDDocumentsUpload } from "../../components/onboard/IDDocumentsUpload";
import { ProfilePhotoUpload } from "../../components/onboard/ProfilePhotoUpload";

/* ─── Helper components ────────────────────────────────────────── */

function Img({ src }: { src: any }) {
  return (
    <div className="textCenter">
      <img alt={APP_NAME} src={src} style={{ maxHeight: 200, maxWidth: 200 }} />
    </div>
  );
}

function StepHeading({ children }: { children: React.ReactNode }) {
  return <div className="font20 bold textDark textCenter">{children}</div>;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flexNullCenter gap8 font14 semiBold"
      style={{
        color: PRIMARY_COLOR,
        padding: "8px 0 4px",
        borderBottom: `1px solid ${PRIMARY_LIGHT}`,
      }}
    >
      {children}
    </div>
  );
}

/* ─── Hook ─────────────────────────────────────────────────────── */

export const useOnboardForm = ({
  form,
  updateFormValues,
}: {
  form: any;
  updateFormValues: any;
}) => {
  const navigate = useNavigate();
  const { updateUser, user } = useAuth();

  // Separate useSet for saving bank/momo after profile (non-blocking)
  const { uploadData: saveBankAccount } = useSet();
  const { uploadData: saveMomoAccount } = useSet();

  const {
    loadData: loadBanks,
    data: ugandanBanksRaw,
    loading: loadingBanks,
  } = useGet();

  const accountType = form?.account_type;
  const idType = form?.id_type;
  const isBorrower = user?.user_type === BORROWER;

  useEffect(() => {
    loadBanks({ api: API_UGANDAN_BANKS });
  }, []);

  const ugandanBanks = Array.isArray(ugandanBanksRaw) ? ugandanBanksRaw : [];

  /* ── Step 0: Account Type ── */
  const step0 = {
    fields: [
      { type: COMPONENT, component: <Img src={Team} /> },
      {
        type: COMPONENT,
        component: <StepHeading>Choose Account Type</StepHeading>,
      },
      {
        type: COMPONENT,
        component: (
          <div className="textCenter font13 grey">
            Are you registering as an <b>Individual</b> or as an{" "}
            <b>Institution</b>?
          </div>
        ),
      },
      {
        type: SELECT_VIEW,
        field: "account_type",
        options: [
          { label: "individual", icon: ProfileIcon },
          { label: "institution", value: "group", icon: GroupIcon },
        ],
        required: true,
      },
    ],
  };

  /* ── Step 1: Profile ── */
  const individualFields = [
    { type: TEXT, field: "first_name", required: true },
    { type: TEXT, field: "last_name", required: true },
    {
      type: TEXT,
      field: "phone_number",
      required: true,
      props: { disabled: true },
    },
    {
      type: TEXT,
      field: "secondary_phone_number",
      label: "Secondary Phone Number",
      props: {
        placeholder: "+256700000000",
        helperText: "Optional — backup contact or MoMo number",
        optional: true,
      },
    },
    {
      type: DATE,
      field: "dateofbirth",
      label: "Date of birth",
      required: true,
    },
    {
      type: SELECT,
      field: "id_type",
      label: "ID_type",
      options: ["national_id", "passport", "drivers_license"].map((v) =>
        addSelectOption(v)
      ),
      required: true,
    },
    {
      type: TEXT,
      field: "nin",
      label: `${idType} number`,
      visible: idType != null,
      required: true,
    },
    // Profile photo (selfie) — doubles as account avatar AND as the
    // admin's "real person" check at KYC time. Writes a File into
    // form.image which the onboard onSubmit FormData branch picks up.
    {
      type: COMPONENT,
      component: (
        <ProfilePhotoUpload form={form} updateFormValues={updateFormValues} />
      ),
    },
    // ID document uploads — the single source of truth for KYC docs.
    // Account page no longer offers a separate upload card; everything
    // lives here so there's one place to do KYC. The custom component
    // writes File objects into form.id_front / form.id_back which the
    // onSubmit detects and sends as multipart.
    {
      type: COMPONENT,
      component: (
        <IDDocumentsUpload form={form} updateFormValues={updateFormValues} />
      ),
    },
  ];

  const institutionFields = [
    {
      type: TEXT,
      field: "first_name",
      label: "Legal Entity Name",
      required: true,
    },
    { type: TEXT, field: "nin", label: "business_registration_number" },
  ];

  const step1 = {
    fields: [
      { type: COMPONENT, component: <Img src={Profile} /> },
      {
        type: COMPONENT,
        component: <StepHeading>Fill Profile Information</StepHeading>,
      },
      ...(accountType === INDIVIDUAL ? individualFields : institutionFields),
    ],
  };

  /* ── Borrower-only step: About your finances ──
     Source of funds is the one compliance answer lending genuinely
     needs; payout details are optional and skippable. Investors never
     see this step — the location step and the investor-profiling
     questionnaire were removed outright (server defaults apply, and
     address/street/postal/district are NOT part of KYC). */
  const financesStep = {
    fields: [
      { type: COMPONENT, component: <Img src={Profile} /> },
      {
        type: COMPONENT,
        component: <StepHeading>About Your Finances</StepHeading>,
      },
      {
        type: SELECT,
        field: "source_of_funds",
        label: "Main source of funds",
        required: true,
        options: [
          "employment",
          "business",
          "investment",
          "savings",
          "other",
        ].map((v) => addSelectOption(v)),
      },
      {
        type: COMPONENT,
        component: (
          <div className="textCenter font13 grey mb10">
            Optionally add where loan disbursements and withdrawals should
            go. You can skip this and add it later.
          </div>
        ),
      },

      // ── Bank Account ──
      {
        type: COMPONENT,
        component: <SectionLabel>Bank Account (optional)</SectionLabel>,
      },
      {
        field: "bank_ugandan_bank",
        label: "Bank",
        type: SELECT,
        options: ugandanBanks.map((b: any) => ({ id: b.id, name: b.name })),
        props: { loading: loadingBanks },
      },
      {
        field: "bank_account_name",
        label: "Account Holder Name",
        type: TEXT,
        visible: !!form?.bank_ugandan_bank,
        props: { placeholder: "Name on the account" },
      },
      {
        field: "bank_account_number",
        label: "Account Number",
        type: TEXT,
        visible: !!form?.bank_ugandan_bank,
        props: { placeholder: "e.g. 9030012345678", inputMode: "numeric" },
      },

      // ── MoMo ──
      {
        type: COMPONENT,
        component: <SectionLabel>Mobile Money (optional)</SectionLabel>,
      },
      {
        field: "momo_provider",
        label: "Provider",
        type: SELECT,
        options: [
          { id: "mtn_momo", name: "MTN Mobile Money" },
          { id: "airtel_money", name: "Airtel Money" },
        ],
      },
      {
        field: "momo_phone",
        label: "MoMo Phone Number",
        type: TEXT,
        visible: !!form?.momo_provider,
        props: { placeholder: "+256700123456" },
      },
      {
        field: "momo_account_name",
        label: "Registered Name",
        type: TEXT,
        visible: !!form?.momo_provider,
        props: { placeholder: "Name registered on MoMo" },
      },

      // Info
      {
        type: COMPONENT,
        component: (
          <InfoBox>
            You can always add or edit payment methods later from{" "}
            <b>Account → Payment Methods</b>.
          </InfoBox>
        ),
      },
    ],
  };

  /* ── All steps — deliberately short so signup doesn't bleed users:
     investors: account type → identity + documents (2 steps)
     borrowers: + one finances step (3 steps) ── */
  const steps = isBorrower ? [step0, step1, financesStep] : [step0, step1];

  /* ── Submit ── */
  const onSubmit = ({ payload }: any) => {
    // Extract payment method data before sending profile update
    const hasBankData =
      payload.bank_ugandan_bank && payload.bank_account_number;
    const hasMomoData = payload.momo_provider && payload.momo_phone;

    const bankData = hasBankData
      ? {
          ugandan_bank: payload.bank_ugandan_bank,
          account_name: payload.bank_account_name || "",
          account_number: payload.bank_account_number,
          bank_name:
            ugandanBanks.find(
              (b: any) => b.id === payload.bank_ugandan_bank
            )?.name || "",
          currency: "UGX",
        }
      : null;

    const momoData = hasMomoData
      ? {
          provider: payload.momo_provider,
          phone_number: payload.momo_phone,
          account_name: payload.momo_account_name || "",
        }
      : null;

    // Clean payment fields from profile payload
    const profilePayload = { ...payload };
    [
      "bank_ugandan_bank",
      "bank_account_name",
      "bank_account_number",
      "momo_provider",
      "momo_phone",
      "momo_account_name",
    ].forEach((k) => delete profilePayload[k]);

    // Never change user_type via onboarding
    delete profilePayload.user_type;

    const data = getChangedFields(user, profilePayload);
    const id = profilePayload?.id;

    // If the onboard form captured ID front/back uploads, send them in
    // the same PATCH as multipart so the user.id_front / id_back fields
    // populate atomically with the rest of the KYC payload.
    const hasFiles =
      profilePayload.id_front instanceof File ||
      profilePayload.id_back instanceof File ||
      profilePayload.image instanceof File;

    let params: any = data;
    if (hasFiles) {
      const fd = new FormData();
      Object.entries(data || {}).forEach(([k, v]) => {
        if (v instanceof File) {
          fd.append(k, v);
        } else if (k === "dateofbirth" && v) {
          // Coerce date → YYYY-MM-DD so Django's DateTimeField parses it.
          const s = typeof v === "string" ? v : (v as any).format?.("YYYY-MM-DD") || String(v);
          fd.append(k, s);
        } else if (v !== undefined && v !== null) {
          fd.append(k, String(v));
        }
      });
      params = fd;
    }

    return {
      id,
      api: API_USERS,
      params,
      successMessage: { message: "KYC Completed successfully", duration: 7 },
      onSuccess: (responseData: any) => {
        updateUser({ ...user, ...responseData });

        // Fire-and-forget bank account save
        if (bankData) {
          saveBankAccount({
            api: API_USER_BANK_ACCOUNTS,
            params: bankData,
          });
        }

        // Fire-and-forget MoMo account save
        if (momoData) {
          saveMomoAccount({
            api: API_USER_MOMO_ACCOUNTS,
            params: momoData,
          });
        }

        navigate(-1);
        showConfetti();
      },
    };
  };

  return { steps, submitText: SAVE, onSubmit };
};














// /**
//  * hooks/onboard/useOnboardForm.ts — UPDATED
//  *
//  * Phase 6 changes:
//  *   - NEW step 4: "Payment Methods" — add secondary phone, bank account, MoMo
//  *   - Existing steps 0-3 unchanged (account type, profile, location, KYC)
//  *   - Profile step now includes secondary_phone_number field
//  *   - Step 4 uses inline forms (not the modal formHooks) since it's
//  *     part of the stepper flow
//  *
//  * REPLACE your existing hooks/onboard/useOnboardForm.ts with this file.
//  */

// import { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//   CardIcon,
//   CaseIcon,
//   Coins,
//   GroupIcon,
//   Profile,
//   ProfileIcon,
//   Team,
// } from "../../assets/svg/Svgs";
// import {
//   BusinessOutline,
//   PhonePortraitOutline,
//   AddCircleOutline,
//   CheckmarkCircle,
//   TrashOutline,
// } from "@raresail/react-ionicons";
// import styled from "styled-components";
// import { alpha } from "@mui/material";
// import { useAuth } from "../../components/context/AuthContext";
// import { API_CITIES, API_COUNTRIES, API_USERS } from "../../constants/apis";
// import {
//   API_UGANDAN_BANKS,
//   API_USER_BANK_ACCOUNTS,
//   API_USER_MOMO_ACCOUNTS,
// } from "../../constants/apis";
// import {
//   APP_NAME,
//   BORROWER,
//   COMPONENT,
//   DATE,
//   INDIVIDUAL,
//   INVESTOR,
//   SAVE,
//   SELECT,
//   SELECT_VIEW,
//   TEXT,
// } from "../../constants/constants";
// import { addSelectOption, getChangedFields } from "../../utils/helpers";
// import useGet from "../common/useGet";
// import { showConfetti } from "../../components/elements/ConfettiView";
// import { InfoBox } from "../../components/elements/Elements";
// import {
//   PRIMARY_COLOR,
//   primary10,
//   primary5,
//   PRIMARY_LIGHT,
// } from "../../constants/colors";

// export const useOnboardForm = ({
//   form,
//   updateFormValues,
// }: {
//   form: any;
//   updateFormValues: any;
// }) => {
//   const navigate = useNavigate();
//   const { updateUser, user } = useAuth();

//   const {
//     loadData: getCountries,
//     loading: loadCountries,
//     data: countries,
//   } = useGet();
//   const { loadData: getCities, loading: loadCities, data: cities } = useGet();
//   const {
//     loadData: loadBanks,
//     data: ugandanBanksRaw,
//     loading: loadingBanks,
//   } = useGet();

//   const accountType = form?.account_type;
//   const idType = form?.id_type;
//   const country_code = countries?.find(
//     (v: any) => v.id === form?.country
//   )?.code2;

//   useEffect(() => {
//     getCountries({ api: API_COUNTRIES });
//     loadBanks({ api: API_UGANDAN_BANKS });
//   }, []);

//   useEffect(() => {
//     if (country_code) getCities({ api: API_CITIES, id: country_code });
//   }, [country_code]);

//   const ugandanBanks = Array.isArray(ugandanBanksRaw) ? ugandanBanksRaw : [];

//   // ── Step 0: Account Type ──
//   const accountTypeFields = [
//     {
//       type: COMPONENT,
//       component: <Img src={Team} />,
//     },
//     {
//       type: COMPONENT,
//       component: <Heading>Choose Account Type</Heading>,
//     },
//     {
//       type: COMPONENT,
//       component: (
//         <div className="textCenter font13 grey">
//           Are you registering as an <b>Individual</b> or as an{" "}
//           <b>Institution</b>?
//         </div>
//       ),
//     },
//     {
//       type: SELECT_VIEW,
//       field: "account_type",
//       options: [
//         { label: "individual", icon: ProfileIcon },
//         { label: "institution", value: "group", icon: GroupIcon },
//       ],
//       required: true,
//     },
//   ];

//   // ── Step 1: Profile (UPDATED - added secondary phone) ──
//   const individualFields = [
//     { type: TEXT, field: "first_name", required: true },
//     { type: TEXT, field: "last_name", required: true },
//     {
//       type: TEXT,
//       field: "phone_number",
//       required: true,
//       props: { disabled: true },
//     },
//     {
//       type: TEXT,
//       field: "secondary_phone_number",
//       label: "Secondary Phone Number",
//       props: {
//         placeholder: "+256700000000",
//         helperText: "Optional — backup contact or MoMo number",
//         optional: true,
//       },
//     },
//     {
//       type: DATE,
//       field: "dateofbirth",
//       label: "Date of birth",
//       required: true,
//     },
//     {
//       type: SELECT,
//       field: "id_type",
//       label: "ID_type",
//       options: ["national_id", "passport", "drivers_license"].map((v) =>
//         addSelectOption(v)
//       ),
//       required: true,
//     },
//     {
//       type: TEXT,
//       field: "nin",
//       label: `${idType} number`,
//       visible: idType != null,
//       required: true,
//     },
//   ];

//   const institutionFields = [
//     {
//       type: TEXT,
//       field: "first_name",
//       label: "Legal Entity Name",
//       required: true,
//     },
//     { type: TEXT, field: "nin", label: "business_registration_number" },
//   ];

//   // ── Step 2: Location ──
//   const locationFields = [
//     { type: COMPONENT, component: <Img src={Profile} /> },
//     { type: COMPONENT, component: <Heading>Your Location Details</Heading> },
//     {
//       type: SELECT,
//       field: "country",
//       options: countries,
//       props: { loading: loadCountries },
//       optionKeys: [{ fieldKey: "country_code", valueKey: "code2" }],
//       required: true,
//     },
//     {
//       type: SELECT,
//       field: "city",
//       options: cities,
//       props: { loading: loadCities },
//       required: true,
//     },
//     { type: TEXT, field: "street", required: true },
//     { type: TEXT, field: "plot_number" },
//     { type: TEXT, field: "postal_code" },
//     { type: TEXT, field: "postal_address" },
//   ];

//   const profileFields = [
//     { type: COMPONENT, component: <Img src={Profile} /> },
//     {
//       type: COMPONENT,
//       component: <Heading>Fill Profile Information</Heading>,
//     },
//     ...(accountType === INDIVIDUAL ? individualFields : institutionFields),
//   ];

//   // ── Step 3: KYC ──
//   const kycFields = [
//     { type: COMPONENT, component: <Img src={Profile} /> },
//     {
//       type: COMPONENT,
//       component: <Heading>Verification (KYC)</Heading>,
//     },
//     {
//       type: SELECT,
//       field: "investment_objective",
//       required: true,
//       options: [
//         "capital_growth",
//         "capital_preservation",
//         "speculation",
//         "other",
//       ].map((v) => addSelectOption(v)),
//     },
//     {
//       type: SELECT,
//       field: "investment_risk",
//       required: true,
//       options: ["low", "medium", "high"].map((v) => addSelectOption(v)),
//     },
//     {
//       type: SELECT,
//       field: "investment_knowledge",
//       required: true,
//       options: ["no_knowledge", "beginner", "intermediate", "advanced"].map(
//         (v) => addSelectOption(v)
//       ),
//     },
//     {
//       type: SELECT,
//       field: "source_of_funds",
//       required: true,
//       options: [
//         "employment",
//         "business",
//         "investment",
//         "savings",
//         "other",
//       ].map((v) => addSelectOption(v)),
//     },
//   ];

//   // ── Step 4: Payment Methods (NEW) ──
//   const paymentMethodsFields = [
//     {
//       type: COMPONENT,
//       component: <Img src={Profile} />,
//     },
//     {
//       type: COMPONENT,
//       component: <Heading>Payment Methods</Heading>,
//     },
//     {
//       type: COMPONENT,
//       component: (
//         <div className="textCenter font13 grey mb10">
//           Add your bank account and/or mobile money details for
//           receiving disbursements and withdrawals. You can skip this
//           step and add them later.
//         </div>
//       ),
//     },

//     // Bank account section
//     {
//       type: COMPONENT,
//       component: (
//         <SectionLabel>
//           <BusinessOutline fontSize={16} />
//           <span>Bank Account</span>
//           <span className="font11 grey">(optional)</span>
//         </SectionLabel>
//       ),
//     },
//     {
//       field: "bank_ugandan_bank",
//       label: "Bank",
//       type: SELECT,
//       options: ugandanBanks.map((b: any) => ({ id: b.id, name: b.name })),
//       props: { loading: loadingBanks },
//     },
//     {
//       field: "bank_account_name",
//       label: "Account Holder Name",
//       type: TEXT,
//       visible: !!form?.bank_ugandan_bank,
//       props: { placeholder: "Name on the account" },
//     },
//     {
//       field: "bank_account_number",
//       label: "Account Number",
//       type: TEXT,
//       visible: !!form?.bank_ugandan_bank,
//       props: { placeholder: "e.g. 9030012345678", inputMode: "numeric" },
//     },

//     // MoMo section
//     {
//       type: COMPONENT,
//       component: (
//         <SectionLabel style={{ marginTop: 10 }}>
//           <PhonePortraitOutline fontSize={16} />
//           <span>Mobile Money</span>
//           <span className="font11 grey">(optional)</span>
//         </SectionLabel>
//       ),
//     },
//     {
//       field: "momo_provider",
//       label: "Provider",
//       type: SELECT,
//       options: [
//         { id: "mtn_momo", name: "MTN Mobile Money" },
//         { id: "airtel_money", name: "Airtel Money" },
//       ],
//     },
//     {
//       field: "momo_phone",
//       label: "MoMo Phone Number",
//       type: TEXT,
//       visible: !!form?.momo_provider,
//       props: { placeholder: "+256700123456" },
//     },
//     {
//       field: "momo_account_name",
//       label: "Registered Name",
//       type: TEXT,
//       visible: !!form?.momo_provider,
//       props: { placeholder: "Name registered on MoMo" },
//     },

//     // Info
//     {
//       type: COMPONENT,
//       component: (
//         <InfoBox>
//           You can always add or edit payment methods later from{" "}
//           <b>Account → Payment Methods</b>.
//         </InfoBox>
//       ),
//     },
//   ];

//   // ── Steps array ──
//   const steps = [
//     { fields: accountTypeFields },
//     { fields: profileFields },
//     { fields: locationFields },
//     { fields: kycFields },
//     { fields: paymentMethodsFields },
//   ];

//   // ── Submit ──
//   const onSubmit = ({ payload }: any) => {
//     // Extract payment method data before sending profile update
//     const bankData = {
//       ugandan_bank: payload.bank_ugandan_bank,
//       account_name: payload.bank_account_name,
//       account_number: payload.bank_account_number,
//       bank_name:
//         ugandanBanks.find((b: any) => b.id === payload.bank_ugandan_bank)
//           ?.name || "",
//       currency: "UGX",
//     };

//     const momoData = {
//       provider: payload.momo_provider,
//       phone_number: payload.momo_phone,
//       account_name: payload.momo_account_name,
//     };

//     // Clean payment fields from profile payload
//     const profilePayload = { ...payload };
//     delete profilePayload.bank_ugandan_bank;
//     delete profilePayload.bank_account_name;
//     delete profilePayload.bank_account_number;
//     delete profilePayload.momo_provider;
//     delete profilePayload.momo_phone;
//     delete profilePayload.momo_account_name;

//     // Never allow user_type to be changed via onboarding
//     delete profilePayload.user_type;

//     const data = getChangedFields(user, profilePayload);
//     const id = profilePayload?.id;

//     return {
//       id,
//       api: API_USERS,
//       params: data,
//       successMessage: { message: "KYC Completed successfully", duration: 7 },
//       onSuccess: async (responseData: any) => {
//         updateUser({ ...user, ...responseData });

//         // Save bank account if provided
//         if (bankData.ugandan_bank && bankData.account_number) {
//           try {
//             const axios = (await import("axios")).default;
//             const { BASE_URL } = await import("../../constants/config");
//             const creds = JSON.parse(
//               localStorage.getItem("DGD^#&#^#*#&#*(^GDGE#^*&*%%%") || "{}"
//             );
//             await axios.post(`${BASE_URL}/${API_USER_BANK_ACCOUNTS}`, bankData, {
//               headers: { Authorization: `Bearer ${creds.access}` },
//             });
//           } catch (e) {
//             console.warn("Bank account save failed (non-blocking):", e);
//           }
//         }

//         // Save MoMo account if provided
//         if (momoData.provider && momoData.phone_number) {
//           try {
//             const axios = (await import("axios")).default;
//             const { BASE_URL } = await import("../../constants/config");
//             const creds = JSON.parse(
//               localStorage.getItem("DGD^#&#^#*#&#*(^GDGE#^*&*%%%") || "{}"
//             );
//             await axios.post(`${BASE_URL}/${API_USER_MOMO_ACCOUNTS}`, momoData, {
//               headers: { Authorization: `Bearer ${creds.access}` },
//             });
//           } catch (e) {
//             console.warn("MoMo account save failed (non-blocking):", e);
//           }
//         }

//         navigate(-1);
//         showConfetti();
//       },
//     };
//   };

//   return { steps, submitText: SAVE, onSubmit };
// };

// /* ─── Helper components ─────────────────────────────────────────── */

// const Img = ({ src }: { src: any }) => (
//   <div className="textCenter">
//     <img
//       alt={APP_NAME}
//       src={src}
//       style={{ maxHeight: 200, maxWidth: 200 }}
//     />
//   </div>
// );

// const HeadingComp = ({ children }: { children?: any }) => (
//   <div className="font20 bold textDark textCenter">{children}</div>
// );

// // Use this instead of Heading to avoid conflict with Elements.Heading
// const Heading = HeadingComp;

// const SectionLabel = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 8px;
//   font-size: 14px;
//   font-weight: 600;
//   color: ${PRIMARY_COLOR};
//   padding: 8px 0 4px;
//   border-bottom: 1px solid ${PRIMARY_LIGHT};
// `;